import React from "react";
import VariantPanel, { type VariantFact } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface CurvedCopy {
  eyebrow: string;
  title: string;
  lead: string;
  points: string[];
  facts: VariantFact[];
  imageAlt: string;
}
const copyData: Record<Lang, CurvedCopy> = {
  en: {
    eyebrow: "Radial Arc Panels • Cross-Flow Dewatering",
    title: "Curved Wedge Wire Screens",
    lead: "Engineered with precise radial curvature and resistance-welded profile wire, MutualFilter curved screens leverage fluid kinetic energy for continuous cross-flow dewatering, pulp fiber recovery, and mineral slurry classification.",
    points: ["Profile wires oriented perpendicular to slurry flow slice thin liquid films continuously with zero mechanical power.", "Two-point edge contact and widening relief angles ensure solids passing the aperture discharge freely without clogging.", "Symmetrical arc panels can be reversed 180° to expose fresh leading edges, doubling operational lifespan in abrasive slurries."],
    facts: [{
      label: "Slot Width",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Standard Arc Angles",
      value: "45° / 60° / 120°"
    }, {
      label: "Screening Action",
      value: "Cross-Flow"
    }, {
      label: "Alloy Grades",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Stainless steel curved wedge wire sieve bend screen panel with side mounting bars"
  },
  zh: {
    eyebrow: "径向弧形筛面 • 切向横流水力脱水",
    title: "不锈钢弧形筛板与筛弯",
    lead: "MutualFilter 弧形筛网采用精密径向弧度与全熔电阻焊工艺，利用物料重力与流体动能实现连续切向水膜剥离，广泛应用于选煤脱水、淀粉提取、造纸纤维回收与矿物分级。",
    points: ["垂直于料流方向的 V 型型材丝形成连续水膜切削剥离，依靠重力与流体动能实现高速固液分离。", "微触双点开孔与后向释放角确保固体微粒穿过表面后顺畅滑落，彻底杜绝深层机械卡塞。", "对称弧形结构支持筛板 180 度翻转安装，重新启用锋利迎水面，在高磨损工况下使有效寿命倍增。"],
    facts: [{
      label: "缝隙宽度",
      value: "0.02 – 5.0 mm"
    }, {
      label: "标准弧度规格",
      value: "45° / 60° / 120°"
    }, {
      label: "分离模式",
      value: "切向脱水 (Cross-Flow)"
    }, {
      label: "标准材质",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "带有侧边安装加强条的不锈钢楔形丝弧形筛板（筛弯）"
  },
  es: {
    eyebrow: "Paneles de arco radial • Filtración tangencial",
    title: "Cribas curvas de alambre en cuña",
    lead: "Diseñadas con curvatura radial precisa y alambre perfilado soldado por resistencia, las cribas curvas aprovechan la energía cinética del fluido para deshidratación tangencial continua, lavado de almidón y clasificación de pulpas.",
    points: ["Los perfiles de alambre perpendiculares al flujo cortan láminas delgadas de líquido de forma continua y sin consumo de energía motriz.", "El contacto en dos puntos y el ángulo de alivio posterior aseguran que los sólidos descarguen libremente sin atascos.", "Los paneles simétricos se pueden invertir 180° para presentar aristas nuevas, duplicando la durabilidad en pulpas abrasivas."],
    facts: [{
      label: "Ancho de ranura",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Ángulos de arco",
      value: "45° / 60° / 120°"
    }, {
      label: "Acción de cribado",
      value: "Flujo cruzado"
    }, {
      label: "Aleaciones",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Panel de criba curva de alambre en cuña de acero inoxidable con barras de fijación laterales"
  },
  de: {
    eyebrow: "Radiale Bogensegmente • Querstrom-Trennung",
    title: "Gekrümmte Spaltsiebe (Bogensebe)",
    lead: "Gefertigt mit präziser radialer Bogenkrümmung und widerstandsgeschweißtem Spaltsiebprofil nutzen Bogensebe die kinetische Strömungsenergie zur kontinuierlichen Entwässerung, Faseraufbereitung und Mineralsuspensionstrennung.",
    points: ["Quer zur Strömungsrichtung angeordnete Profildrähte schälen dünne Flüssigkeitsfilme kontinuierlich und ohne mechanischen Antrieb ab.", "Zweipunktkontakt und erweiterter Freiwinkel gewährleisten ungehinderten Feststoffaustrag ohne Verstopfungsrisiko.", "Symmetrische Bogensegmente lassen sich um 180° wenden, um neue Schnittkanten zu nutzen und die Standzeit bei abrasiven Schlämmen zu verdoppeln."],
    facts: [{
      label: "Spaltweite",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Bogenwinkel",
      value: "45° / 60° / 120°"
    }, {
      label: "Trennverfahren",
      value: "Querstrom (Cross-Flow)"
    }, {
      label: "Werkstoffe",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Edelstahl-Spaltsiebbogen mit seitlichen Montageleisten für Bogensiebe"
  },
  fr: {
    eyebrow: "Panneaux courbes radiaux • Égouttage tangentiel",
    title: "Grilles courbes en fil profilé",
    lead: "Conçues avec une courbure radiale précise et un profilé en V soudé par résistance, les grilles courbes exploitent l'énergie cinétique du fluide pour l'égouttage continu, la récupération de fibres et la classification minérale.",
    points: ["Les fils profilés perpendiculaires au flux cisaillent les films liquides en continu sans apport d'énergie mécanique.", "Le contact en deux points et le dépouillement arrière assurent l'évacuation libre des solides sans colmatage.", "Les panneaux arqués symétriques peuvent être retournés à 180° pour réutiliser de nouvelles arêtes d'attaque en milieu abrasif."],
    facts: [{
      label: "Largeur de fente",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Angles d'arc",
      value: "45° / 60° / 120°"
    }, {
      label: "Principe",
      value: "Flux croisé (Cross-Flow)"
    }, {
      label: "Nuances d'alliage",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Panneau de grille courbe en fil profilé inox avec barres de fixation latérales"
  },
  pt: {
    eyebrow: "Painéis de arco radial • Filtração tangencial",
    title: "Telas curvas de arame em cunha",
    lead: "Projetadas com curvatura radial precisa e arame perfilado soldado por resistência, as telas curvas utilizam a energia cinética do fluido para desaguamento tangencial contínuo, lavagem de amido e classificação de polpas.",
    points: ["Arames perfilados perpendiculares ao fluxo cortam continuamente películas de líquido sem gasto de energia mecânica.", "Contato em dois pontos e ângulo de alívio traseiro garantem que os sólidos descarreguem livremente sem entupimento.", "Painéis de arco simétrico podem ser invertidos em 180° para expor novas arestas de corte, duplicando a vida útil em polpas abrasivas."],
    facts: [{
      label: "Largura de fenda",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Ângulos de arco",
      value: "45° / 60° / 120°"
    }, {
      label: "Ação de separação",
      value: "Fluxo cruzado"
    }, {
      label: "Ligas de aço",
      value: "AISI 304 / 316L"
    }],
    imageAlt: "Painel de tela curva de arame em cunha de aço inoxidável com barras de fixação laterais"
  }
};
export default function CurvedGroup() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <VariantPanel id="curved-screens" href="/CurvedScreensPage" eyebrow={copy.eyebrow} title={copy.title} lead={copy.lead} points={copy.points} facts={copy.facts} image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-curved-wedg-0074a0.png",
    alt: copy.imageAlt
  }} tone="muted" />;
}